import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clearAuth, PLATFORM_PROJECT_CODE, readAuth, type StoredAuth, writeAuth } from './authStorage';
import { authApi, type LoginResponse } from '../api/AuthApi';

export type AuthUser = StoredAuth['user'];

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  /** Platform staff: members of the platform project, including while impersonating. */
  isPlatform: boolean;
  isImpersonating: boolean;
  login(input: { username: string; password: string }): Promise<StoredAuth>;
  register(input: { name: string; email: string; password: string }): Promise<StoredAuth>;
  switchProject(projectId: string): Promise<void>;
  logout(): void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function toStoredAuth(data: LoginResponse, impersonating: boolean): StoredAuth {
  return {
    token: data.accessToken,
    impersonating,
    user: {
      id: data.user.userId,
      name: data.user.name,
      email: data.user.email || data.user.username,
      role: data.user.role,
      projectId: data.project?.projectId ?? (data.user.projectId as string | undefined),
      projectCode: data.project?.code,
      roleAccess: data.user.roleAccess as StoredAuth['user']['roleAccess'],
    },
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    const stored = readAuth();
    if (stored?.token && stored?.user) {
      setUser(stored.user);
      setToken(stored.token);
      setImpersonating(Boolean(stored.impersonating));
      setStatus('authenticated');
    } else {
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isAuthenticated = status === 'authenticated';
    // Owners of ordinary projects never manage projects, no matter what their
    // roleAccess flags say — the platform project is the only home for that.
    const isPlatform =
      isAuthenticated &&
      (user?.role === 'batamx' ||
        user?.projectCode?.toLowerCase() === PLATFORM_PROJECT_CODE ||
        impersonating);

    return {
      status,
      user,
      token,
      isAuthenticated,
      isPlatform,
      isImpersonating: impersonating,

      async login({ username, password }) {
        const { data } = await authApi.login({ username, password });

        const stored = toStoredAuth(data, false);
        setUser(stored.user);
        setToken(stored.token);
        setImpersonating(false);
        setStatus('authenticated');
        writeAuth(stored);
        return stored;
      },

      async register({ name, email, password }) {
        await authApi.register({ name, email, password, username: email });

        const { data } = await authApi.login({ username: email, password });

        const stored = toStoredAuth(data, false);
        setUser(stored.user);
        setToken(stored.token);
        setImpersonating(false);
        setStatus('authenticated');
        writeAuth(stored);
        return stored;
      },

      async switchProject(projectId: string) {
        const { data } = await authApi.changeProject(projectId);

        // The new token impersonates the target project's owner; keep the platform
        // privileges so the session can switch again without logging out.
        const stored = toStoredAuth(data, true);
        setUser(stored.user);
        setToken(stored.token);
        setImpersonating(true);
        writeAuth(stored);
      },

      logout() {
        authApi.logout().catch(() => {});
        setUser(null);
        setToken(null);
        setImpersonating(false);
        setStatus('anonymous');
        clearAuth();
      },
    };
  }, [status, user, token, impersonating]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
